import React, { useState } from 'react';
import { generateSSOToken } from '../services/api';

export default function DealerDashboard({ dealer, onLogout }) {
  const [ucc, setUcc]           = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [recentUCCs, setRecentUCCs] = useState([]);
  const [toast, setToast]       = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleSSO = async (uccValue) => {
    const targetUCC = (uccValue || ucc).trim().toUpperCase();
    if (!targetUCC) return setError('Please enter a UCC.');
    setLoading(true); setError('');
    try {
      const res = await generateSSOToken(targetUCC);
      const { ssoUrl, client } = res.data;

      // Add to recent
      setRecentUCCs(prev => {
        const filtered = prev.filter(r => r.ucc !== targetUCC);
        return [{ ucc: targetUCC, name: client, time: new Date() }, ...filtered].slice(0, 10);
      });

      showToast(`Opening ${client} (${targetUCC}) in new tab...`);
      window.open(ssoUrl, '_blank');
      setUcc('');
    } catch (err) {
      setError(err.response?.data?.error || 'UCC not found or SSO failed.');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'Segoe UI', Arial, sans-serif" }}>

      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, background: '#f0fdf4', border: '0.5px solid #bbf7d0', color: '#15803d', padding: '10px 18px', borderRadius: 8, fontSize: 13, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
          {toast.msg}
        </div>
      )}

      {/* Topbar */}
      <div style={{ background: '#fff', borderBottom: '0.5px solid #e2e8f0', padding: '0 28px', height: 52, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 28, height: 28, background: '#1d4ed8', borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: '#fff', fontWeight: 800, fontSize: 12 }}>N</span>
          </div>
          <span style={{ color: '#0f172a', fontWeight: 700, fontSize: 14 }}>Navia Backup</span>
          <span style={{ color: '#cbd5e1', fontSize: 13 }}>|</span>
          <span style={{ color: '#64748b', fontSize: 12 }}>Dealer Portal</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ background: '#eff6ff', color: '#1d4ed8', border: '0.5px solid #bfdbfe', padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
            {dealer.dealer_id}
          </span>
          <span style={{ fontSize: 12, color: '#64748b' }}>{dealer.name}</span>
          <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#1d4ed8', color: '#fff', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {dealer.name[0]}
          </div>
          <button onClick={() => { localStorage.removeItem('nb_dealer_token'); localStorage.removeItem('nb_dealer'); onLogout(); }}
            style={{ padding: '6px 14px', borderRadius: 6, border: '0.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 11, cursor: 'pointer' }}>
            Sign Out
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 800, margin: '40px auto', padding: '0 24px' }}>

        {/* UCC Search Card */}
        <div style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 14, padding: '32px', marginBottom: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <h2 style={{ color: '#0f172a', fontSize: 18, fontWeight: 700, margin: '0 0 6px' }}>Client UCC Search</h2>
          <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 24px' }}>
            Enter client UCC to open their positions and holdings in a new tab via SSO.
          </p>

          <div style={{ display: 'flex', gap: 10 }}>
            <input
              type="text"
              value={ucc}
              onChange={e => { setUcc(e.target.value.toUpperCase()); setError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleSSO()}
              placeholder="Enter Client UCC e.g. 97917850"
              style={{
                flex: 1, padding: '12px 16px', borderRadius: 8,
                border: `1.5px solid ${error ? '#fecaca' : '#e2e8f0'}`,
                background: '#f8fafc', color: '#0f172a', fontSize: 15,
                fontWeight: 500, outline: 'none', boxSizing: 'border-box'
              }}
              onFocus={e => e.target.style.borderColor = '#1d4ed8'}
              onBlur={e => e.target.style.borderColor = error ? '#fecaca' : '#e2e8f0'}
            />
            <button
              onClick={() => handleSSO()}
              disabled={loading || !ucc.trim()}
              style={{
                padding: '12px 28px', borderRadius: 8, border: 'none',
                background: loading || !ucc.trim() ? '#93c5fd' : '#1d4ed8',
                color: '#fff', fontSize: 14, fontWeight: 600,
                cursor: loading || !ucc.trim() ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(29,78,216,0.25)'
              }}
            >
              {loading ? 'Opening...' : '↗ Open Client'}
            </button>
          </div>

          {error && (
            <div style={{ marginTop: 10, color: '#dc2626', fontSize: 12, background: '#fff5f5', border: '0.5px solid #fecaca', padding: '8px 12px', borderRadius: 6 }}>
              {error}
            </div>
          )}

          <div style={{ marginTop: 16, padding: '12px 16px', background: '#f8fafc', borderRadius: 8, border: '0.5px solid #e2e8f0' }}>
            <div style={{ fontSize: 11, color: '#64748b', lineHeight: 1.7 }}>
              <strong style={{ color: '#374151' }}>How it works:</strong> Enter UCC → Click Open Client → Client page opens in new tab with your Dealer ID (<strong>{dealer.dealer_id}</strong>) tracked → You can view positions/holdings and place sq-off on behalf of the client.
            </div>
          </div>
        </div>

        {/* Recent UCCs */}
        {recentUCCs.length > 0 && (
          <div style={{ background: '#fff', border: '0.5px solid #e2e8f0', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <div style={{ padding: '14px 20px', borderBottom: '0.5px solid #e2e8f0' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>Recent Clients Accessed</div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Click to reopen in new tab</div>
            </div>
            {recentUCCs.map((r, i) => (
              <div key={r.ucc} onClick={() => handleSSO(r.ucc)} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 20px', cursor: 'pointer',
                borderBottom: i < recentUCCs.length - 1 ? '0.5px solid #f1f5f9' : 'none',
                background: '#fff', transition: 'background 0.15s'
              }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8faff'}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#eff6ff', color: '#1d4ed8', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {r.name ? r.name[0] : 'C'}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: '#0f172a' }}>{r.name || r.ucc}</div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>UCC: {r.ucc}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 10, color: '#94a3b8' }}>
                    {new Date(r.time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span style={{ fontSize: 11, color: '#1d4ed8' }}>↗ Open</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}